import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  InteractionObjectType,
  InteractionType,
  ObjectDocumentObjectType,
  ObjectDocumentSource,
  ProjectIssuanceTransactionConstants,
} from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData, SelectData, UploadedDocument } from "../../../../models";
import { createIssuanceTransaction } from "../../../../service/project";
import {
  GetProjectIssuancesResponse,
  searchInteractions,
  SearchIssuanceTransactionsResponse,
} from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { convertDateToStringDDMMYYYY, isOwnFormSubmit, useIsLoadingWrapper } from "../../../../utils";
import { useObjectAttachments } from "../../../../utils/hooks/useObjectAttachments";
import { dataGridMapFilterCriteria, DocumentListDocument, Toast } from "../../../../widget";
import { InteractionTag } from "../../../TagsPanel/models";
import { useIssuanceTransactionPermission } from "../../hooks/useIssuanceTransactionPermission";
import { saveTransactionTags } from "../../utils/saveTransactionTags";

/*
  Copy for the one-shot rule (BR-02841). A quote is taken up once: whatever is not committed here is released
  back to available units rather than staying quoted, so the user has to see that before choosing a quantity.
*/
const QUOTE_PROGRESSION_NOTICE =
  "Progressing a quote closes it. If you commit fewer units than the quote covers, the remainder is released back to available inventory rather than staying quoted.";

interface UseProgressTransactionPanelProps {
  show: boolean;
  // The transaction being progressed. The panel is only opened from a row, so this is always present.
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  issuance?: GetProjectIssuancesResponse["results"][0];
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseProgressTransactionPanelReturnData {
  panelData: ProjectIssuanceTransactionPanelData;
  setPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  typeData: SelectData;
  settlementWarning: string | null;
  settlementWarningTitle: string | null;
  isFormLocked: boolean;
  progressionNotice: string | null;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  // True while the parent's inherited tags are being read; a submit is refused until it clears so none are dropped.
  isTagsLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: (e: FormEvent) => Promise<void>;

  showReleaseToInventoryModal: boolean;
  releaseToInventoryType: string | undefined;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  changedTagsExternal: InteractionTag[];
  setChangedTagsExternal: Dispatch<SetStateAction<InteractionTag[]>>;
  setRefreshTags: Dispatch<SetStateAction<boolean>>;
  tagPills: PillData[];

  documents: DocumentListDocument[];
  canManageAttachments: boolean;
  // Whether the form may be changed and confirmed at all — false without manageProjectIssuances.
  canEdit: boolean;
  projectUuid: string | null;
  showUploadDocumentModal: boolean;
  handleAddAttachment: () => void;
  handleCloseUploadDocumentModal: () => void;
  handleDocumentsUploaded: (documents: UploadedDocument[]) => Promise<void>;
  handleRemoveAttachment: (documentUuid: string) => Promise<void>;
  handleDownloadAttachment: (documentUuid: string) => Promise<void>;
}

const seedPanelData = (
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0]
): ProjectIssuanceTransactionPanelData => ({
  ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
  issuanceUuid: issuanceTransaction.issuanceUuid,
  parentTransactionUuid: issuanceTransaction.uuid,
  /*
    Carried from the parent. The uuid is gated on MANAGE_COLLABORATOR server-side, so a parent whose company
    the caller cannot manage arrives as a name with nothing behind it — see TransactionCompanyPicker.
  */
  counterPartyCollaboratorUuid: issuanceTransaction.counterParty.uuid,
  counterPartyName: issuanceTransaction.counterParty.name,
  brokerCollaboratorUuid: issuanceTransaction.broker.uuid,
  brokerName: issuanceTransaction.broker.name,
  /*
    Defaults to what is left to progress, not the parent's original size — adjustedQuantity is its quantity
    less everything already progressed out of it. Seeding the full quantity on a partly-progressed parent
    opened the panel already over the limit the server enforces.
  */
  quantity: issuanceTransaction.adjustedQuantity ?? issuanceTransaction.quantity ?? null,
  price: issuanceTransaction.price ?? null,
  fees: issuanceTransaction.fees ?? null,
  totalValue: issuanceTransaction.totalValue ?? null,
  areaHa: issuanceTransaction.areaHa ?? null,
  /*
    Not a field on this overlay — a progression is recorded as happening now, not on a date the user picks.
    Set here because transaction_date is NOT NULL, so the create would otherwise be rejected with nothing on
    the form to fix. ReleaseToInventoryModal reads the same panelData, so its create is covered too.
  */
  transactionDate: new Date(),
});

export const useProgressTransactionPanel = ({
  show,
  issuanceTransaction,
  issuance,
  onClose,
  handleRefresh,
}: UseProgressTransactionPanelProps): UseProgressTransactionPanelReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const [showReleaseToInventoryModal, setShowReleaseToInventoryModal] = useState(false);
  const [releaseToInventoryType, setReleaseToInventoryType] = useState<string>();

  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [initialTagsFetched, setInitialTagsFetched] = useState(false);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [changedTagsExternal, setChangedTagsExternal] = useState<InteractionTag[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [uploadToken, setUploadToken] = useState(0);

  const [panelData, setPanelData] = useState<ProjectIssuanceTransactionPanelData>(seedPanelData(issuanceTransaction));

  const projectUuid = issuanceTransaction.project?.uuid ?? null;
  /*
    Resolved rather than read from ProjectContext alone: the org-wide inventory dashboard renders this panel
    outside ProjectProvider, where the context cannot answer. See useIssuanceTransactionPermission.
  */
  const { canManageIssuances } = useIssuanceTransactionPermission(projectUuid, show);
  const canManageAttachments = canManageIssuances;

  /*
    The progressed transaction does not exist yet, so uploads are staged against the panel and linked by
    saveAttachments once the create returns a uuid.
  */
  const {
    attachments,
    beginUpload,
    handleDocumentsUploaded: linkUploadedDocuments,
    handleRemoveAttachment,
    handleDownloadAttachment,
    saveAttachments,
    resetAttachments,
  } = useObjectAttachments({
    objectUuid: undefined,
    objectType: ObjectDocumentObjectType.ISSUANCE_TRANSACTION,
    documentSource: ObjectDocumentSource.PROJECT_DOCUMENT,
    documentScope: "project",
    parentUuid: projectUuid,
    subjectLabel: "transaction",
  });

  const documents: DocumentListDocument[] = attachments.map((attachment) => ({
    documentUuid: attachment.documentUuid,
    fileName: attachment.fileName,
    documentType: attachment.documentType ?? undefined,
    uploadedAt: attachment.uploadedAt ? convertDateToStringDDMMYYYY(attachment.uploadedAt) : undefined,
    url: attachment.url ?? undefined,
  }));

  const isQuoteParent =
    issuanceTransaction.type === ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_QUOTE;

  const committedQuantity = panelData.quantity ?? 0;
  const availableUnits = issuance?.unitQuantityAvailable;
  /*
    The parent's remaining balance, not its original size: adjustedQuantity is its quantity less everything
    already progressed out of it, which is the same ceiling the server enforces (it sums the existing children
    rather than comparing against the parent alone). Falls back to quantity for a row without it.
  */
  const remainingParentQuantity = issuanceTransaction.adjustedQuantity ?? issuanceTransaction.quantity ?? 0;

  const isCancellation = panelData.type === ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED;

  const hasInsufficientUnitsError =
    errors?.some((error) => error.code === "VALIDATION_INSUFFICIENT_AVAILABLE_UNITS") ?? false;
  /*
    BR-02842: committing a quote newly consumes availability, because a quote never deducted any — so an
    over-quoted issuance cannot be over-committed, and once availability is exhausted the only progression
    left is Cancelled. Every other parent type already moved its units out of the available pool when it was
    created, so Reserved -> Sold consumes nothing and must not be measured against that pool: doing so marked
    valid reservations as overcommitted whenever the issuance had less availability left than the reservation
    covered. Keyed on the parent type alone, deliberately not on panelData.type — typeData is derived from
    isOverCommitted below, so selecting Sold would narrow the option list out from under the selection.
  */
  const exceedsAvailable = isQuoteParent && availableUnits !== undefined && committedQuantity > availableUnits;
  const exceedsParent = committedQuantity > remainingParentQuantity;

  const isOverCommitted = exceedsAvailable || exceedsParent || hasInsufficientUnitsError;

  const parentLabel = issuanceTransaction.type?.toLowerCase() ?? "transaction";

  /*
    Two different blocks with two different ways out, so they cannot share copy. Exhausted availability is
    terminal — cancelling is the only exit. A quantity above what the parent covers is just a number the user
    typed, and reducing it is the ordinary fix, so saying "can only be cancelled" there sends them the wrong way.
  */
  let settlementWarning: string | null = null;
  let settlementWarningTitle: string | null = null;
  if (!isCancellation && exceedsAvailable) {
    settlementWarningTitle = "This issuance can no longer be progressed";
    settlementWarning = `This ${parentLabel} covers ${committedQuantity} units but only ${availableUnits} are available. It can no longer be progressed, and can only be cancelled.`;
  } else if (!isCancellation && exceedsParent) {
    settlementWarningTitle = "This is more than the transaction covers";
    // "remaining" rather than "covers": a partly-progressed parent has less left than it was created for.
    settlementWarning = `You cannot commit more than the ${remainingParentQuantity} units remaining on this ${parentLabel}. Reduce the quantity, or cancel it instead.`;
  } else if (!isCancellation && hasInsufficientUnitsError) {
    settlementWarningTitle = "This issuance can no longer be progressed";
    settlementWarning = errors?.[0]?.message ?? "There are not enough available units to progress this transaction.";
  }

  /*
    Locks the fields that cannot resolve an over-commitment. Type, Quantity and Confirm stay live throughout:
    the block is cleared either by cancelling or by committing fewer units, and both have to remain reachable.
  */
  const isFormLocked = isOverCommitted && !isCancellation;

  const typeData = ProjectIssuanceTransactionConstants.getFollowOnTypeSelectData(
    issuanceTransaction.type,
    isOverCommitted
  );
  const progressionNotice = isQuoteParent ? QUOTE_PROGRESSION_NOTICE : null;

  const handleClosePanel = useCallback((): void => {
    onClose();
    setPanelData(seedPanelData(issuanceTransaction));
    setShowReleaseToInventoryModal(false);
    setReleaseToInventoryType(undefined);
    setShowUploadDocumentModal(false);
    setInitialTagsFetched(false);
    setTagPills([]);
    setChangedTagsExternal([]);
    setErrors([]);
    resetAttachments();
    // resetAttachments is stable in effect but not in identity; the rest is all this needs to reset the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuanceTransaction, onClose]);

  const handleAddAttachment = (): void => {
    setUploadToken(beginUpload());
    setShowUploadDocumentModal(true);
  };

  const handleCloseUploadDocumentModal = (): void => {
    setShowUploadDocumentModal(false);
  };

  const handleDocumentsUploaded = async (uploaded: UploadedDocument[]): Promise<void> => {
    setShowUploadDocumentModal(false);
    await linkUploadedDocuments(uploaded, uploadToken);
  };

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!isOwnFormSubmit(e)) return;
    setErrors([]);

    /*
      Confirm stays live so a blocked submit can say what is wrong, rather than leaving the user to infer it
      from a button that does not respond. Codes are deliberately unlike VALIDATION_INSUFFICIENT_AVAILABLE_UNITS,
      which hasInsufficientUnitsError reads to drive the settlement warning.
    */
    const nextErrors: ServiceError[] = [];

    if (!canManageIssuances) {
      nextErrors.push({
        code: "REQUIRED_MANAGE_PROJECT_ISSUANCES",
        message: "You do not have permission to progress transactions on this project.",
      });
    }

    /*
      Submitting mid-load created the follow-on with none of the parent's tags, and there is no second chance
      to attach them — so this still blocks, but now explains itself.
    */
    if (isTagsLoading) {
      nextErrors.push({
        code: "TAGS_STILL_LOADING",
        message: "The tags on this transaction are still loading. Wait a moment and try again.",
      });
    }

    // A follow-on has no sensible default type, so there is nothing to post until one is chosen.
    if (!panelData.type) {
      nextErrors.push({
        code: "REQUIRED_TRANSACTION_TYPE",
        message: "Select the type to progress this transaction into.",
      });
    }

    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }

    const res = await createIssuanceTransaction({
      issuanceTransactions: [
        {
          areaHa: panelData.areaHa,
          counterPartyCollaboratorUuid: panelData.counterPartyCollaboratorUuid,
          brokerCollaboratorUuid: panelData.brokerCollaboratorUuid,
          expiryDate: panelData.expiryDate,
          externalReference: panelData.externalReference,
          fees: panelData.fees,
          issuanceUuid: panelData.issuanceUuid ?? "",
          parentTransactionUuid: panelData.parentTransactionUuid,
          price: panelData.price,
          quantity: panelData.quantity ?? 0,
          totalValue: panelData.totalValue !== null ? parseFloat(panelData.totalValue.toFixed(5)) : null,
          transactionDate: panelData.transactionDate,
          type: panelData.type ?? "",
        },
      ],
    });

    if (res.status === Status.Error) {
      setErrors(res.errors);
      return;
    }

    const createdTransactionUuid = res.data?.issuanceTransactions?.[0]?.uuid;

    const tagErrors = createdTransactionUuid
      ? await saveTransactionTags(createdTransactionUuid, changedTagsExternal)
      : undefined;
    const attachmentErrors = createdTransactionUuid ? await saveAttachments(createdTransactionUuid) : undefined;

    Toast.success({ message: "Issuance transaction created successfully" });

    /*
      Both partial failures are reported before the release-to-inventory branch below, which returns early —
      the progressed transaction is already persisted either way, and a tagging failure matters most on exactly
      that path, where the user is about to be asked a follow-on question about the same transaction.
    */
    if (tagErrors) {
      Toast.error({ message: `The transaction was saved, but its tags could not be added: ${tagErrors[0].message}` });
    }

    if (attachmentErrors) {
      Toast.error({
        message: `The transaction was saved, but its attachments could not: ${attachmentErrors[0].message}`,
      });
    }

    /*
      BR-02841 — progressing a quote is one-shot, so the remainder is released without asking. Only a
      reservation or sale, which can be left partly open, offers the choice.
    */
    const hasRemainder = remainingParentQuantity > committedQuantity;
    if (hasRemainder && !isQuoteParent) {
      setShowReleaseToInventoryModal(true);
      setReleaseToInventoryType(issuanceTransaction.type);
      return;
    }

    handleRefresh();
    handleClosePanel();
  }, setIsHandleSubmitLoading);

  /*
    The inherited tags are read asynchronously but submitted from a value captured when Confirm is pressed, so
    the load has to be visible to the submit lifecycle — isTagsLoading refuses a submit until it settles.
    Without that, confirming before this resolved created the follow-on with changedTagsExternal still empty,
    permanently dropping the parent's tags. Not wrapped in useIsLoadingWrapper: that returns a fresh closure
    each render, and this is a dependency of the effect below, which would then re-fetch on every render.
  */
  const fetchTags = useCallback(async (uuid: string, orgUuid: string): Promise<void> => {
    setIsTagsLoading(true);

    try {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = { operator: "eq", value: InteractionType.TAG };
      filterCriteria.objectType = { operator: "eq", value: InteractionObjectType.ORGANISATION };
      filterCriteria.objectUuid = { operator: "eq", value: orgUuid };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: { operator: "eq", value: uuid },
          endDate: { operator: "eq", value: null },
        },
      };

      const response = await searchInteractions({
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: { results: filterCriteria },
      });

      if (response.data?.results != null) {
        const existingTags = response.data.results.map(
          (x) =>
            ({
              uuid: x.uuid,
              data: JSON.parse(x.data),
              checked: true,
              rowVersion: x.rowVersion,
            }) as InteractionTag
        );
        setTagPills(existingTags.map((tag) => ({ key: tag.uuid, label: tag.data.name })));
        setChangedTagsExternal(existingTags);
      } else {
        /*
          Say so rather than silently progressing without them: the user cannot tell an untagged parent from
          one whose tags failed to load, and the follow-on would be created missing them either way.
        */
        Toast.error({ message: "The tags on this transaction could not be loaded, so they will not be carried over." });
      }
    } finally {
      /*
        Settled either way. Flipped in finally rather than only on success so a failed load stops the effect
        re-requesting on every render, and so Confirm is never left permanently disabled.
      */
      setInitialTagsFetched(true);
      setIsTagsLoading(false);
    }
  }, []);

  // Re-seeds whenever the panel opens or moves to another transaction.
  useEffect(() => {
    setPanelData(seedPanelData(issuanceTransaction));
    setErrors([]);
  }, [issuanceTransaction, show]);

  // The progressed transaction inherits the tags of the one it progresses.
  useEffect(() => {
    if (show && !initialTagsFetched && currentOrganisationUuid) {
      fetchTags(issuanceTransaction.uuid, currentOrganisationUuid);
    } else if (show) {
      setTagPills(changedTagsExternal.filter((x) => x.checked).map((x) => ({ key: x.uuid, label: x.data.name })));
    }
  }, [
    show,
    issuanceTransaction.uuid,
    currentOrganisationUuid,
    refreshTags,
    changedTagsExternal,
    fetchTags,
    initialTagsFetched,
  ]);

  return {
    panelData,
    setPanelData,
    typeData,
    settlementWarning,
    settlementWarningTitle,
    isFormLocked,
    progressionNotice,
    errors,
    isHandleSubmitLoading,
    isTagsLoading,
    handleClosePanel,
    handleSubmit,
    showReleaseToInventoryModal,
    releaseToInventoryType,
    showTagsPanel,
    setShowTagsPanel,
    changedTagsExternal,
    setChangedTagsExternal,
    setRefreshTags,
    tagPills,
    documents,
    canManageAttachments,
    canEdit: canManageIssuances,
    projectUuid,
    showUploadDocumentModal,
    handleAddAttachment,
    handleCloseUploadDocumentModal,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
  };
};
